/**
 * Hash sidebar nav catalog. Mirrors the office system's data shape so a
 * customer on both products sees one mental model. Labels are i18n keys
 * (resolved with `t()` at render time) so the same catalog drives both
 * locales without a parallel array. The QuickSearch palette and the
 * keyboard shortcut runtime both consume this catalog directly.
 */

import type { ReactNode } from 'react';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CategoryIcon from '@mui/icons-material/Category';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaidIcon from '@mui/icons-material/Paid';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PeopleIcon from '@mui/icons-material/People';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SettingsIcon from '@mui/icons-material/Settings';
import FolderIcon from '@mui/icons-material/Folder';
import StorageIcon from '@mui/icons-material/Storage';
import SchemaIcon from '@mui/icons-material/Schema';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import RouterIcon from '@mui/icons-material/Router';
import HubIcon from '@mui/icons-material/Hub';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import PaletteIcon from '@mui/icons-material/Palette';
import SavingsIcon from '@mui/icons-material/Savings';
import SummarizeIcon from '@mui/icons-material/Summarize';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import InsightsIcon from '@mui/icons-material/Insights';
import TimelineIcon from '@mui/icons-material/Timeline';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

export interface NavItem {
  to: string;
  label: string;            // i18n key or literal Hebrew
  icon: ReactNode;
  /** Footer-only: hidden when user.is_admin is false. */
  adminOnly?: boolean;
}

export interface NavSection {
  /** Stable key for persisting open/closed state in localStorage. */
  key: string;
  title: string;
  /** Icon rendered next to the section title in the header — same
   * size/treatment as office, so a multi-product user gets one mental
   * model. */
  sectionIcon?: ReactNode;
  items: NavItem[];
}

export const SECTIONS: NavSection[] = [
  {
    key: 'main',
    title: 'nav.sections.main',
    sectionIcon: <DashboardIcon fontSize="small" />,
    items: [
      { to: '/dashboard',     label: 'nav.dashboard',     icon: <DashboardIcon /> },
      { to: '/client-summary', label: 'סיכום לקוח',        icon: <SummarizeIcon /> },
      { to: '/firm-digest',   label: 'דיווח מצב משרד',     icon: <InsightsIcon /> },
      { to: '/filing-calendar', label: 'לוח דיווחים',      icon: <EventNoteIcon /> },
      { to: '/transaction-search', label: 'חיפוש תנועות',  icon: <ReceiptLongIcon /> },
      { to: '/inbox',         label: 'הדואר שלי',          icon: <DashboardIcon /> },
      { to: '/guides',        label: 'מדריכי שימוש',       icon: <MenuBookIcon /> },
      { to: '/chat',          label: 'nav.chat',          icon: <SmartToyIcon /> },
      { to: '/audits',        label: 'nav.audits',        icon: <FactCheckIcon /> },
      { to: '/cross-audits',  label: 'nav.crossAudits',   icon: <WarningAmberIcon /> },
    ],
  },
  {
    key: 'statements',
    title: 'nav.sections.statements',
    sectionIcon: <AssessmentIcon fontSize="small" />,
    items: [
      { to: '/profit-loss',               label: 'nav.profitLoss',           icon: <AssessmentIcon /> },
      { to: '/profit-loss-compare',       label: 'nav.profitLossCompare',    icon: <CompareArrowsIcon /> },
      { to: '/cross-profit-loss-compare', label: 'nav.crossProfitLoss',      icon: <CompareArrowsIcon /> },
      { to: '/cross-revenue-compare',     label: 'nav.crossRevenueCompare',  icon: <StackedLineChartIcon /> },
      { to: '/annual-report',             label: 'nav.annualReport',         icon: <DescriptionIcon /> },
      { to: '/trial-balance',             label: 'nav.trialBalance',         icon: <AccountTreeIcon /> },
      { to: '/tb-audit',                  label: 'מאזן בוחן — ביקורת',       icon: <AccountTreeIcon /> },
      { to: '/sample',                    label: 'בחירת דגימה',              icon: <AccountTreeIcon /> },
      { to: '/cash-flow-audit',           label: 'תזרים מזומנים',            icon: <AccountTreeIcon /> },
      { to: '/bank-recon',                label: 'nav.bankRecon',            icon: <CompareArrowsIcon /> },
    ],
  },
  {
    key: 'books',
    title: 'nav.sections.books',
    sectionIcon: <MenuBookIcon fontSize="small" />,
    items: [
      { to: '/journal',              label: 'nav.journal',                icon: <MenuBookIcon /> },
      { to: '/accounts',             label: 'nav.accounts',               icon: <AccountBalanceIcon /> },
      { to: '/year-end-adjustments', label: 'nav.yearEndAdjustments',     icon: <MenuBookIcon /> },
      { to: '/aje',                  label: 'רישומים מתואמים',           icon: <MenuBookIcon /> },
      { to: '/doc-to-je',            label: 'מסמך → רישום',              icon: <MenuBookIcon /> },
      { to: '/detail-report',        label: 'nav.detailReport',           icon: <AccountTreeIcon /> },
      { to: '/master-tree',          label: 'nav.masterTree',             icon: <AccountTreeIcon /> },
      { to: '/classification',       label: 'nav.classification',         icon: <CategoryIcon /> },
      { to: '/classification/main-accounts', label: 'סיווג חשבונות ראשיים', icon: <CategoryIcon /> },
    ],
  },
  {
    key: 'operations',
    title: 'nav.sections.operations',
    sectionIcon: <ReceiptLongIcon fontSize="small" />,
    items: [
      { to: '/sales',           label: 'nav.sales',          icon: <ShoppingCartIcon /> },
      { to: '/expenses',        label: 'nav.expenses',       icon: <ReceiptLongIcon /> },
      { to: '/salary-expenses', label: 'nav.salaryExpenses', icon: <PaidIcon /> },
      { to: '/owner-loans',     label: 'nav.ownerLoans',     icon: <AccountBalanceOutlinedIcon /> },
      { to: '/customers',       label: 'nav.customers',      icon: <PeopleIcon /> },
      { to: '/suppliers',       label: 'nav.suppliers',      icon: <LocalShippingIcon /> },
      { to: '/cashflow',        label: 'nav.cashflow',       icon: <AccountBalanceWalletIcon /> },
      { to: '/aging',           label: 'nav.aging',          icon: <AccessTimeIcon /> },
    ],
  },
  {
    key: 'analysis',
    title: 'nav.sections.analysis',
    sectionIcon: <WarningAmberIcon fontSize="small" />,
    items: [
      { to: '/period-breakdown',  label: 'הוצאות/הכנסות לתקופה',  icon: <CompareArrowsIcon /> },
      { to: '/cross-vendor',      label: 'ספק בין לקוחות',         icon: <CompareArrowsIcon /> },
      { to: '/tax-plan',          label: 'תכנון מס שנתי',          icon: <ReceiptLongIcon /> },
      { to: '/category-mapping',  label: 'מיפוי קטגוריות',         icon: <CategoryIcon /> },
      { to: '/anomalies',      label: 'nav.anomalies',         icon: <WarningAmberIcon /> },
      { to: '/comparisons',    label: 'nav.comparisons',       icon: <CompareArrowsIcon /> },
      { to: '/documents',      label: 'nav.documents',         icon: <FolderIcon /> },
      { to: '/raw',            label: 'nav.rawData',           icon: <StorageIcon /> },
      { to: '/wiz-companies',  label: 'nav.wizCompanies',      icon: <StorageIcon /> },
      { to: '/activity',       label: 'פעילות',                icon: <EventNoteIcon /> },
      { to: '/il-tax',         label: 'דוחות מס',              icon: <ReceiptLongIcon /> },
      { to: '/form-6111',      label: 'טופס 6111 + חוות דעת',  icon: <ReceiptLongIcon /> },
      { to: '/checklist',      label: 'תוכנית ביקורת AI',       icon: <ReceiptLongIcon /> },
    ],
  },
  // Hashavshevet extended-data modules — surfacing data the rest of the
  // app doesn't use (VAT 874, bank reconciliation, partner 360, ...).
  // Per-company pages: they read the selected company's Hashavshevet DB.
  {
    key: 'wizdata',
    title: 'תובנות חשבשבת',
    sectionIcon: <ReceiptLongIcon fontSize="small" />,
    items: [
      { to: '/wiz-portfolio', label: 'מצב לקוחות המשרד', icon: <AssessmentIcon /> },
      { to: '/wiz-firm',      label: 'ניהול המשרד (כל הלקוחות)', icon: <BusinessIcon /> },
      { to: '/wiz-search',    label: 'חיפוש על-משרדי',      icon: <TravelExploreIcon /> },
      { to: '/wiz-reasonableness', label: 'בקרת סבירות חשבונאית', icon: <FactCheckIcon /> },
      { to: '/wiz-alerts',    label: 'התראות (כל הלקוחות)', icon: <WarningAmberIcon /> },
      { to: '/wiz-health',    label: 'בריאות חיבורים',     icon: <MonitorHeartIcon /> },
      { to: '/wiz-snapshot',  label: 'מגמת המשרד',         icon: <TimelineIcon /> },
      { to: '/wiz-account360', label: 'תיק חשבון 360',       icon: <PeopleIcon /> },
      { to: '/wiz-vat',       label: 'מע״מ — דוח 874', icon: <ReceiptLongIcon /> },
      { to: '/wiz-bank',      label: 'בנקים והתאמות',   icon: <AccountBalanceIcon /> },
      { to: '/wiz-partners',  label: 'לקוחות וספקים 360', icon: <PeopleIcon /> },
      { to: '/wiz-bookkeeping', label: 'איכות הנהלת חשבונות', icon: <FactCheckIcon /> },
      { to: '/wiz-audit',     label: 'יומן ביקורת ופעילות', icon: <EventNoteIcon /> },
      { to: '/wiz-fx',        label: 'מטבע חוץ ושערים',    icon: <CompareArrowsIcon /> },
      { to: '/wiz-inventory', label: 'מלאי ופריטים',       icon: <CategoryIcon /> },
      { to: '/wiz-close',     label: 'סגירת חודש',          icon: <FactCheckIcon /> },
      { to: '/wiz-statements', label: 'דוחות כספיים (חי)',     icon: <AssessmentIcon /> },
      { to: '/wiz-trial',      label: 'מאזן בוחן',            icon: <AccountBalanceIcon /> },
      { to: '/wiz-ledger',     label: 'חוקר תנועות',          icon: <ReceiptLongIcon /> },
      { to: '/wiz-report',     label: 'דוח מנהלים (PDF/Excel)', icon: <SummarizeIcon /> },
      { to: '/wiz-analysis',  label: 'ניתוח פיננסי',         icon: <StackedLineChartIcon /> },
      { to: '/wiz-pnltrend',  label: 'מגמת רווח חודשית',     icon: <StackedLineChartIcon /> },
      { to: '/wiz-compare',   label: 'השוואות',              icon: <CompareArrowsIcon /> },
      { to: '/wiz-aging',     label: 'גיול חובות',          icon: <AccessTimeIcon /> },
      { to: '/wiz-planning',  label: 'תכנון וגבייה',        icon: <SavingsIcon /> },
      { to: '/wiz-treasury',  label: 'תזרים ויתרות בנק',    icon: <AccountBalanceWalletIcon /> },
      { to: '/wiz-costcenter', label: 'מרכזי עלות וסניפים', icon: <AccountTreeIcon /> },
      { to: '/wiz-withholding', label: 'ניכוי מס במקור (856)', icon: <ReceiptLongIcon /> },
      { to: '/wiz-cards',      label: 'כרטיסי אשראי',        icon: <PaidIcon /> },
      { to: '/wiz-docs',       label: 'כיסוי מסמכים',         icon: <DescriptionIcon /> },
      { to: '/wiz-forensics',  label: 'בדיקות עומק (פורנזיקה)', icon: <WarningAmberIcon /> },
      { to: '/wiz-compliance', label: 'ציות ובקרה',           icon: <FactCheckIcon /> },
      { to: '/wiz-ai',         label: 'עוזר AI לחשבשבת',      icon: <SmartToyIcon /> },
      { to: '/wiz-smart',      label: 'סיוע חכם',             icon: <SmartToyIcon /> },
    ],
  },
  // Account & system settings: collapsed by default so the
  // common-case "audit a client" nav doesn't have to scroll past
  // them. Same pattern as the office system.
  {
    key: 'system',
    title: 'nav.sections.system',
    sectionIcon: <SettingsIcon fontSize="small" />,
    items: [
      { to: '/personalize',        label: 'התאמה אישית',           icon: <PaletteIcon /> },
      { to: '/settings',           label: 'הגדרות',                icon: <SettingsIcon /> },
      { to: '/notifications',      label: 'התראות וגיבוי',         icon: <SettingsIcon />, adminOnly: true },
      { to: '/security',           label: 'אבטחה',                 icon: <SettingsIcon /> },
      { to: '/users',              label: 'nav.users',             icon: <GroupIcon />,   adminOnly: true },
      { to: '/wiz-host',           label: 'nav.wizHost',           icon: <RouterIcon />,  adminOnly: true },
      { to: '/wiz-explorer',       label: 'nav.wizExplorer',       icon: <SchemaIcon />,  adminOnly: true },
      { to: '/wiz-observability',  label: 'תפעול וביקורת גישה',    icon: <InsightsIcon />, adminOnly: true },
      { to: '/office-integration', label: 'nav.officeIntegration', icon: <HubIcon />,     adminOnly: true },
      { to: '/ops',                label: 'ניהול מערכת',          icon: <EventNoteIcon />, adminOnly: true },
    ],
  },
];

/** Footer items — used to be a flat list under the sections. They
 * moved into the SECTIONS.system block above so they collapse like
 * everything else. Kept as an empty export for back-compat with the
 * sidebar render and the QuickSearch palette flatten helper. */
export const FOOTER_ITEMS: NavItem[] = [];

export function activeMatch(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

/** Pick the longest matching candidate so e.g. /expenses/foo activates
 * the /expenses row even when /expenses/category/x also exists. */
export function pickActive(pathname: string, candidates: string[]): string | null {
  let best: string | null = null;
  for (const c of candidates) {
    if (activeMatch(pathname, c) && (best === null || c.length > best.length)) best = c;
  }
  return best;
}

export function flattenItems(includeFooter = true): NavItem[] {
  const out: NavItem[] = [];
  for (const s of SECTIONS) for (const i of s.items) out.push(i);
  if (includeFooter) for (const i of FOOTER_ITEMS) out.push(i);
  return out;
}
